import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { getPageName, getUiInterface, getSuccessMessage, getErrorMessage, getWordConnector } from "../../../locales/translationUtils";
import { useTranslation } from 'react-i18next';
import { useClient } from "../../../apis/client/ClientProvider";
import { useSupplier } from "../../../apis/supplier/SupplierProvider";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import ConfirmDeleteModal from "../../../components/ConfirmDeleteModal";
import Supplier from "../../../types/supplier";
import { FaChevronDown } from "react-icons/fa";
import AlertOverlay from "../../../components/Alert";

const ViewSupplierById: React.FC = () => {
    const { t } = useTranslation();
    const [alert, setAlert] = useState<{
        message: string;
        variant: "success" | "danger" | "warning" | "info";
    } | null>(null);
    const { id } = useParams<{ id: string }>();
    const { fetchSupplierById, excluiSupplier, putSupplier } = useSupplier();
    const { fetchClient, clients } = useClient();
    const [supplier, setSupplier] = useState<Supplier | null>(null);
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const selectRef = useRef<(HTMLSelectElement | null)>(null);
    const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());

    const navItems: NavBarItem[] = [
        { label: t('supplier.labels.home'), route: "/", iconName: "house-door" },
        { label: t('supplier.labels.list'), route: "/Supplier", iconName: "list" },
        { label: t('supplier.labels.new'), route: "/Supplier/create", iconName: "plus-circle" },
    ];

    useEffect(() => {
        if (!id) throw new Error("ID inválido");

        const loadSupplier = async () => {
            try {
                setLoading(true);
                setError(false);

                if (!id) throw new Error("ID inválido");

                const data = await fetchSupplierById(Number(id));

                fetchClient(null);

                setSupplier(data);
            } catch (err) {
                console.error("Erro ao carregar os dados:", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        loadSupplier();
    }, [id, fetchSupplierById, fetchClient]);
    
    const handleClientChange = (e: React.ChangeEvent<any>) => {
        const { value } = e.target;

        setSupplier((prevSupplier) => {
            return {
                ...prevSupplier,
                client: {
                    id: value
                }
            }
        });
    };

    const handleEdit = async () => {
        if (!supplier) return;

        if (isEditing && supplier.id) {
            try {
                const nullRequiredFields: string[] = [];
        
                if (!supplier.name) nullRequiredFields.push("Name");
                if (!supplier.client?.id) nullRequiredFields.push("Client");

                if (nullRequiredFields.length > 0) {
                    setInvalidFields(new Set(nullRequiredFields));
                    const warnMessage: string =
                        nullRequiredFields.length === 1
                            ? getUiInterface("fieldRequiredSingle", {
                                field: nullRequiredFields[0],
                            })
                            : getUiInterface("fieldRequiredMultiple", {
                                fields: `${nullRequiredFields.slice(0, -1).join(", ")}${nullRequiredFields.length > 1
                                    ? ` ${getWordConnector("and")} `
                                    : ""
                                    }${nullRequiredFields.slice(-1)}`,
                            });

                    setAlert({ message: warnMessage, variant: "danger" });
                    return;
                } else {
                    setInvalidFields(new Set());
                }

                const sucesso = await putSupplier(supplier.id, supplier);

                if (sucesso) {
                    const successMessage = getSuccessMessage("editSupplier", { id: supplier.id, });
                    navigate(`/Supplier`, {
                        state: {
                            message: successMessage,
                            variant: "success",
                        },
                    });
                } else {
                    const errorMessage = getErrorMessage("editSupplierError", { id: supplier.id, });
                    setAlert({ message: errorMessage, variant: "danger" });
                }
            } catch (error) {
                setAlert({ message: "Erro ao atualizar os dados.", variant: "danger" });
            }
        } else {
            setIsEditing(true);
        }
    };


    const handleDelete = async () => {
        if (!id) return;

        try {
            const sucesso = await excluiSupplier(Number(id));

            if (sucesso) {
                const successMessage = getSuccessMessage("deleteSupplier", { id: id });
                navigate(`/supplier`, {
                    state: {
                        message: successMessage,
                        variant: "success",
                    },
                });
            }
            else {
                const errorMessage = getErrorMessage("deleteSupplierError", { id: id, });
                setAlert({ message: errorMessage, variant: "danger" });
            }
        } catch (error) {
            setAlert({ message: "Erro ao excluir o registro.", variant: "danger" });
        }
    };


    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (supplier) {
            const { name, value } = e.target;
    
            setSupplier((prevSupplier) => {
                const keys = name.split(".");
                const updatedSupplier = { ...prevSupplier };
                let current: any = updatedSupplier;
    
                for (let i = 0; i < keys.length - 1; i++) {
                    const key = keys[i];
                    current[key] = { ...current[key] };
                    current = current[key];
                }
    
                current[keys[keys.length - 1]] = value;
                
                return updatedSupplier;
            });
        }
    };

    if (loading) {
        return <div className="text-center mt-4">Carregando...</div>;
    }

    if (error || !supplier) {
        return <div className="text-center mt-4 text-danger">Erro ao carregar os dados.</div>;
    }

    return (
        <Container fluid>
            <NavBar items={navItems} />
            {alert && (
                <AlertOverlay
                    message={alert.message}
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                />
            )}
            <div className="mb-3"></div>
            <h2>{isEditing ? getPageName("editSupplier") : getPageName("viewSupplier")}</h2>

            {successMessage && (
                <div style={{ fontSize: "14px", color: "green", marginTop: "5px" }}>
                    {successMessage}
                </div>
            )}

            <Form className="mt-3">
                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group>
                            <Form.Label>{t('supplier.fields.id')}</Form.Label>
                            <Form.Control size="sm" readOnly value={supplier.id} style={{ width: "100%", backgroundColor: "#f0f0f0" }} />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="name">
                            <Form.Label>{t('supplier.fields.name')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={supplier.name}
                                isInvalid={invalidFields.has("Name")}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>

                    {/* <Col md={4} className="mb-3">
                        <Form.Group controlId="cnpj">
                            <Form.Label>CNPJ</Form.Label>
                            <Form.Control
                                type="text"
                                name="cnpj"
                                value={supplier.cnpj}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col> */}

                    <Col md={6} className="mb-3">
                        <Form.Group controlId="client">
                            <Form.Label>{t('supplier.fields.client')}</Form.Label>
                            <div style={{display: "flex", alignItems: "center"}}>
                                {
                                    isEditing ? 
                                        <>
                                            <Form.Control
                                                as="select"
                                                name="client"
                                                value={supplier.client?.id}
                                                isInvalid={invalidFields.has("Client")}
                                                onChange={handleClientChange}
                                                readOnly={!isEditing}
                                                ref={(el: HTMLSelectElement | null) => {
                                                    if (el) selectRef.current = el;
                                                }}
                                                style={{ 
                                                    backgroundImage: 'none', 
                                                    appearance: "none", 
                                                    paddingRight: "30px",
                                                    cursor: "pointer" 
                                                }}
                                            >
                                                <option value="">{t('supplier.labels.selectClient')}</option>
                                                {
                                                    (clients || []).map((client) => (
                                                        <option
                                                            key={client.id}
                                                            value={client.id}>
                                                            {`Id: ${client.id} | Nome: ${client.name}`}
                                                        </option>
                                                    ))
                                                }
                                            </Form.Control>
                                            <FaChevronDown
                                                style={{
                                                    position: "absolute",
                                                    right: "20px",
                                                    pointerEvents: "auto",
                                                    cursor: "pointer",
                                                }}
                                                onClick={() => selectRef?.current?.focus()}
                                            />
                                        </>
                                    :
                                        <>
                                            <Form.Control
                                                type="text"
                                                name="client2"
                                                value={(supplier.client) ? `Id: ${supplier.client?.id} | Nome: ${supplier.client?.name}` : ""}
                                                readOnly={isEditing}
                                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                                            />
                                        </>
                                }
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
                
                {/* <Row>
                    <h5>Location</h5>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="lat">
                            <Form.Label>Lat</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.latitude"
                                value={supplier.location?.latitude}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="lng">
                            <Form.Label>Lng</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.longitude"
                                value={supplier.location?.longitude}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <h6>Address</h6>
                    <Col md={5} className="mb-3">
                        <Form.Group controlId="street">
                            <Form.Label>Street</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.street"
                                value={supplier.location?.address?.street}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2} className="mb-3">
                        <Form.Group controlId="number">
                            <Form.Label>Number</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.number"
                                value={supplier.location?.address?.number}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={5} className="mb-3">
                        <Form.Group controlId="complement">
                            <Form.Label>Complement</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.complement"
                                value={supplier.location?.address?.complement}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="city">
                            <Form.Label>City</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.city"
                                value={supplier.location?.address?.city}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="state">
                            <Form.Label>State</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.state"
                                value={supplier.location?.address?.state}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                
                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="country">
                            <Form.Label>Country</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.country"
                                value={supplier.location?.address?.country}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="zipCode">
                            <Form.Label>Zip Code</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.zipCode"
                                value={supplier.location?.address?.zipCode}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                </Row> */}
            </Form>

            <div className="mt-3 mb-3 d-flex gap-2">
                <Button variant="primary" onClick={handleEdit}>
                    {isEditing ? getUiInterface("save") : getUiInterface("edit")}
                </Button>
                <Button variant="danger" onClick={() => setShowDeleteModal(true)}>{getUiInterface("delete")}</Button>
            </div>

            <ConfirmDeleteModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
            />
        </Container>
    );
};

export default ViewSupplierById;
